// src/pages/Tools/components/ToolSection.tsx
// Replaces: .features-section-tool, .how-it-works-tool, .use-cases-tool-section,
//           .faq-section-tool, .related-tools-section,
//           .features-container-tool, .how-container-tool, .use-cases-container-tool,
//           .faq-container-tool, .related-container-tool,
//           all h2 inside those sections, .section-description-tool,
//           .features-grid-tool, .steps-grid-tool, .use-cases-grid-tool, .related-tools-grid

import React from 'react';
import { cn } from '../../../lib/utils';

type GridVariant = 'features' | 'steps' | 'related' | 'none';

interface ToolSectionProps {
    children: React.ReactNode;
    /**
     * light → white background (how-it-works, faq)
     * muted → #f8f9fa background (features, use-cases, related)
     */
    variant?: 'light' | 'muted';
    /** Section heading (h2) */
    title?: string;
    /** Subtitle paragraph below heading */
    description?: string;
    /**
     * Grid layout for the children:
     *   features/use-cases → auto-fill minmax(240px,1fr) gap-[20px] text-left
     *   steps              → auto-fill minmax(240px,1fr) gap-[32px] text-center
     *   related            → auto-fill minmax(280px,1fr) gap-[20px]
     *   none               → no grid wrapper
     */
    grid?: GridVariant;
    /** aria-labelledby id for the section */
    ariaLabelledBy?: string;
    className?: string;
}

/*
 * Section backgrounds:
 *   .features-section-tool / .use-cases-tool-section / .related-tools-section: background: #f8f9fa
 *   .how-it-works-tool / .faq-section-tool: background: white
 *   All: padding: 64px 0;
 *
 * Container inside section:
 *   max-width: 1000px; margin: 0 auto; padding: 0 24px; text-align: center;
 *
 * h2 inside section:
 *   font-size: 1.75rem; font-weight: 400; color: #202124; margin: 0 0 12px;
 *
 * .section-description-tool:
 *   font-size: 1rem; color: #5f6368; margin: 0 0 40px; line-height: 1.7;
 *   max-width: 680px; margin-left/right: auto;
 *
 * Grid variants:
 *   features/use-cases: grid auto-fill minmax(240px,1fr) gap-[20px] text-left
 *   steps: grid auto-fill minmax(240px,1fr) gap-[32px] text-center
 *   related: grid auto-fill minmax(280px,1fr) gap-[20px]
 */
const gridClasses: Record<GridVariant, string> = {
    features: 'grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-[20px] text-left',
    steps: 'grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-[32px] text-center',
    related: 'grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-[20px]',
    none: '',
};

const ToolSection: React.FC<ToolSectionProps> = ({
    children,
    variant = 'muted',
    title,
    description,
    grid = 'none',
    ariaLabelledBy,
    className,
}) => (
    <section
        className={cn(
            'py-[64px]',
            variant === 'light' ? 'bg-white' : 'bg-[#f8f9fa]',
            className,
        )}
        aria-labelledby={ariaLabelledBy}
    >
        <div className="max-w-[1000px] mx-auto px-[24px] text-center">
            {title && (
                <h2 className="text-[1.75rem] font-normal text-[#202124] m-0 mb-[12px]">
                    {title}
                </h2>
            )}
            {description && (
                <p className="text-[1rem] text-[#5f6368] mb-[40px] leading-[1.7] max-w-[680px] mx-auto mt-0">
                    {description}
                </p>
            )}

            {grid !== 'none' ? (
                <div className={gridClasses[grid]}>
                    {children}
                </div>
            ) : (
                children
            )}
        </div>
    </section>
);

export default ToolSection;
